import { FC, useEffect, useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Alert, Card, Col, Row, Space, Spin, Statistic, Tag, Typography, List, Badge, Button, Tooltip,
} from 'antd'
import {
  UserOutlined, StarOutlined, CheckCircleOutlined, SafetyCertificateOutlined,
  DashboardOutlined, FieldTimeOutlined, HeartOutlined, ThunderboltOutlined,
  ReloadOutlined, RightOutlined, BellOutlined, HistoryOutlined,
} from '@ant-design/icons'
import {
  listMembers, listUserRecStats, listPoolCronStatus, listMatchLogs, listPushLogs,
  listEncounters, Member, UserRecStats, PoolMatchLog, PoolPushLog, AdminEncounter,
} from '@/api/poolAdminApi'
import { jingqueAdminApi, QueueInfo } from '@/api/jingqueAdminApi'
import { usePoolActivity } from '@/context/PoolActivityContext'
import { TimeAgo } from '@/utils/time'

const { Title, Text } = Typography

interface DashData {
  members: Member[]
  stats: UserRecStats[]
  statsTotal: number
  queues: QueueInfo[]
  cronStuck: string[]
  eduPending: number
  encounters: AdminEncounter[]
  matchLogs: PoolMatchLog[]
  pushLogs: PoolPushLog[]
}

interface FeedItem {
  key: string
  time?: string
  icon: React.ReactNode
  text: React.ReactNode
  tag?: React.ReactNode
}

export const DashboardPage: FC = () => {
  const navigate = useNavigate()
  const { currentPoolActivityId, poolActivities } = usePoolActivity()
  const [data, setData] = useState<DashData | null>(null)
  const [loading, setLoading] = useState(true)
  const [errors, setErrors] = useState<string[]>([])

  const load = async () => {
    setLoading(true)
    setErrors([])
    const errs: string[] = []
    const pick = async <T,>(p: Promise<T>, label: string, fallback: T): Promise<T> => {
      try {
        return await p
      } catch (e: any) {
        errs.push(`${label}: ${e?.message || '加载失败'}`)
        return fallback
      }
    }
    const activityId = currentPoolActivityId || undefined
    const [members, statsRes, queueRes, cronRes, eduRes, encRes, logRes, pushRes] =
      await Promise.all([
        pick(listMembers({ limit: 200 }), '池成员', { members: [] as Member[] }),
        pick(listUserRecStats({ activity_id: activityId, limit: 200 }), '匹配统计', {
          stats: [] as UserRecStats[],
          total: 0,
        }),
        pick(jingqueAdminApi.queue.overview(), '队列', { queues: [] as QueueInfo[] }),
        pick(listPoolCronStatus(), '定时任务', { crons: [] }),
        pick(jingqueAdminApi.eduVerify.list(1, 1, 'pending'), '学历审核', {
          items: [],
          total: 0,
          page: 1,
          page_size: 1,
        }),
        pick(listEncounters({ activity_id: activityId, limit: 100 }), '见面', {
          encounters: [] as AdminEncounter[],
        }),
        pick(listMatchLogs({ activity_id: activityId, limit: 10 }), '匹配日志', {
          logs: [] as PoolMatchLog[],
          total: 0,
        }),
        pick(listPushLogs({ limit: 10 }), '推送记录', {
          logs: [] as PoolPushLog[],
          total: 0,
        }),
      ])
    setData({
      members: members.members,
      stats: statsRes.stats,
      statsTotal: statsRes.total,
      queues: queueRes.queues || [],
      cronStuck: (cronRes.crons || []).filter(c => !c.is_alive).map(c => c.name),
      eduPending: eduRes.total,
      encounters: encRes.encounters,
      matchLogs: logRes.logs,
      pushLogs: pushRes.logs,
    })
    setErrors(errs)
    setLoading(false)
  }

  useEffect(() => {
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPoolActivityId])

  const agg = useMemo(() => {
    if (!data) return null
    const active = data.members.filter(m => m.status === 'active').length
    const sum = (f: (s: UserRecStats) => number) => data.stats.reduce((s, x) => s + f(x), 0)
    const pushed = sum(s => s.pushed_count)
    const accepted = sum(s => s.accept_count)
    const responded = sum(s => s.responded_count)
    const pending = sum(s => s.pending_count)
    const errUsers = data.stats.filter(s => s.ever_error).length
    const degradedUsers = data.stats.filter(s => s.ever_degraded).length
    const queueFailed = data.queues.reduce((s, q) => s + (q.failed || 0), 0)
    const queueBacklog = data.queues.reduce((s, q) => s + (q.pending || 0) + (q.retry || 0), 0)
    const upcomingEnc = data.encounters.filter(
      e => e.status === 'pending' || e.status === 'confirmed',
    ).length
    return {
      active, memberTotal: data.members.length, pushed, accepted, responded, pending,
      acceptRate: responded > 0 ? Math.round((accepted / responded) * 100) : null,
      errUsers, degradedUsers, queueFailed, queueBacklog, upcomingEnc,
      eduPending: data.eduPending, cronStuck: data.cronStuck,
    }
  }, [data])

  const feed = useMemo<FeedItem[]>(() => {
    if (!data) return []
    const items: FeedItem[] = []
    for (const l of data.matchLogs) {
      items.push({
        key: `m-${l.id}`,
        time: l.created_at,
        icon: <HistoryOutlined />,
        text: (
          <span>
            <Text strong>{l.nickname || l.user_id}</Text> 触发匹配（{l.trigger_source}），推荐{' '}
            {l.recommendations_count} 人
          </span>
        ),
        tag: l.error_message ? (
          <Tag color="red">失败</Tag>
        ) : l.is_degraded ? (
          <Tag color="orange">降级</Tag>
        ) : (
          <Tag color="green">成功</Tag>
        ),
      })
    }
    for (const p of data.pushLogs) {
      items.push({
        key: `p-${p.id}`,
        time: p.created_at,
        icon: <BellOutlined />,
        text: (
          <span>
            向 <Text strong>{p.nickname || p.user_id}</Text>
            {p.target_nickname || p.target_user_id
              ? ` 推送 ${p.target_nickname || p.target_user_id}`
              : ''}{' '}
            的推荐
          </span>
        ),
        tag:
          p.log_type === 'error' ? (
            <Tag color="red">推送失败</Tag>
          ) : p.log_type === 'skip' ? (
            <Tag>跳过</Tag>
          ) : (
            <Tag color="blue">已推送</Tag>
          ),
      })
    }
    return items
      .sort((a, b) => (b.time || '').localeCompare(a.time || ''))
      .slice(0, 12)
  }, [data])

  const poolName = poolActivities.find(a => a.id === currentPoolActivityId)?.name

  const kpis: Array<{
    title: string
    value: number | string
    suffix?: React.ReactNode
    icon: React.ReactNode
    color?: string
    to: string
    hint?: string
  }> = agg
    ? [
        {
          title: '活跃成员', value: agg.active, icon: <UserOutlined />, to: '/pool/members',
          suffix: <Text type="secondary" style={{ fontSize: 13 }}>/ {agg.memberTotal}</Text>,
          hint: '池中状态为 active 的成员数',
        },
        {
          title: '待响应推荐', value: agg.pending, icon: <StarOutlined />, to: '/pool/recommendations?status=pending',
          hint: '已生成但未推送/未响应的推荐',
        },
        {
          title: '推荐接受率', value: agg.acceptRate === null ? '-' : `${agg.acceptRate}%`,
          icon: <CheckCircleOutlined />, to: '/pool/user-match-stats',
          hint: `已推送 ${agg.pushed}，已响应 ${agg.responded}，接受 ${agg.accepted}`,
        },
        {
          title: '近期见面安排', value: agg.upcomingEnc, icon: <HeartOutlined />, to: '/pool/encounters',
          hint: '状态为待确认/已确认的见面',
        },
        {
          title: '待审核学历', value: agg.eduPending ?? 0, icon: <SafetyCertificateOutlined />,
          color: (agg?.eduPending ?? 0) > 0 ? '#cf1322' : undefined,
          to: '/edu-verify-review', hint: '等待人工审核的学历认证',
        },
        {
          title: '队列失败任务', value: agg.queueFailed, icon: <DashboardOutlined />,
          color: agg.queueFailed > 0 ? '#cf1322' : undefined, to: '/queue-monitor',
          suffix: <Text type="secondary" style={{ fontSize: 13 }}>积压 {agg.queueBacklog}</Text>,
        },
        {
          title: '定时任务异常', value: agg.cronStuck?.length ?? 0, icon: <FieldTimeOutlined />,
          color: (agg.cronStuck?.length ?? 0) > 0 ? '#fa8c16' : undefined, to: '/pool/cron-status',
          hint: (agg.cronStuck ?? []).join('、') || undefined,
        },
        {
          title: '匹配异常用户', value: agg.errUsers, icon: <ThunderboltOutlined />,
          color: agg.errUsers > 0 ? '#fa8c16' : undefined, to: '/pool/user-match-stats',
          suffix: <Text type="secondary" style={{ fontSize: 13 }}>降级 {agg.degradedUsers}</Text>,
        },
      ]
    : []

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space direction="vertical" size={0}>
          <Title level={4} style={{ margin: 0 }}>业务总览</Title>
          <Text type="secondary">
            {poolName ? `当前池子：${poolName} · ` : ''}聚合各子系统关键指标，点击卡片直达明细
          </Text>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
      </Space>

      {errors.length > 0 && (
        <Alert
          type="warning" showIcon closable style={{ marginBottom: 16 }}
          message="部分数据源加载失败"
          description={errors.join('；')}
        />
      )}

      <Spin spinning={loading && !data}>
        <Row gutter={[16, 16]}>
          {kpis.map(k => (
            <Col xs={12} sm={8} md={6} key={k.title}>
              <Tooltip title={k.hint}>
                <Card
                  hoverable
                  onClick={() => navigate(k.to)}
                  styles={{ body: { padding: 16 } }}
                >
                  <Statistic
                    title={
                      <Space size={6}>
                        <span style={{ color: k.color || '#6366f1' }}>{k.icon}</span>
                        {k.title}
                      </Space>
                    }
                    value={k.value}
                    valueStyle={{ color: k.color }}
                    suffix={k.suffix}
                  />
                </Card>
              </Tooltip>
            </Col>
          ))}
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={14}>
            <Card
              title="最新动态"
              extra={
                <Space split="|" size={4}>
                  <Link to="/pool/match-logs">匹配日志</Link>
                  <Link to="/pool/push-logs">推送记录</Link>
                </Space>
              }
              styles={{ body: { paddingTop: 8 } }}
            >
              <List
                size="small"
                dataSource={feed}
                locale={{ emptyText: '暂无动态' }}
                renderItem={item => (
                  <List.Item style={{ padding: '8px 0' }}>
                    <Space size={8} style={{ flex: 1, minWidth: 0 }}>
                      <Text type="secondary">{item.icon}</Text>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.text}
                      </span>
                      {item.tag}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      <TimeAgo iso={item.time} />
                    </Text>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card title="快捷入口" styles={{ body: { paddingTop: 8 } }}>
              <List
                size="small"
                dataSource={[
                  { to: '/pool/todo', label: '待办中心', desc: '需要人工介入的异常与提醒' },
                  { to: '/pool/search', label: '用户搜索', desc: '按条件/标签检索用户并查看画像' },
                  { to: '/pool/members', label: '池成员', desc: '成员状态与套餐管理' },
                  { to: '/pool/user-match-stats', label: '用户匹配概览', desc: '按用户聚合的匹配漏斗' },
                  { to: '/pool/trigger-match', label: '手动触发匹配', desc: '为指定用户立即跑匹配' },
                  { to: '/edu-verify-review', label: '学历审核', desc: '处理待审核认证', badge: agg?.eduPending },
                  { to: '/queue-monitor', label: '队列监控', desc: '任务积压与失败处理', badge: agg?.queueFailed },
                  { to: '/pool/cron-status', label: '定时任务', desc: '匹配 Pipeline 健康检查', badge: agg?.cronStuck?.length },
                  { to: '/activities', label: '活动管理', desc: '活动报名/签到/反馈' },
                ]}
                renderItem={item => (
                  <List.Item style={{ padding: '8px 0' }}>
                    <Link to={item.to} style={{ flex: 1 }}>
                      <Space size={8}>
                        <Text strong>{item.label}</Text>
                        {item.badge ? <Badge count={item.badge} size="small" /> : null}
                        <Text type="secondary" style={{ fontSize: 12 }}>{item.desc}</Text>
                      </Space>
                    </Link>
                    <RightOutlined style={{ color: 'rgba(0,0,0,0.25)', fontSize: 12 }} />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  )
}
